import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bike, MapPin, Package, Zap } from "lucide-react";
import { toast } from "sonner";
import { adminRiderApi, type DeliveryOrder, type Rider } from "@/features/delivery/api";
import { formatApiError } from "@/features/auth/formatError";

const rupee = (n: number) => `₹${n.toLocaleString("en-IN")}`;

export default function AdminDispatch() {
    const qc = useQueryClient();

    const { data: unassigned = [], isLoading } = useQuery<DeliveryOrder[]>({
        queryKey: ["admin", "dispatch", "unassigned"],
        queryFn: adminRiderApi.unassigned,
        refetchInterval: 8000,
    });
    const { data: onlineRiders } = useQuery({
        queryKey: ["admin", "dispatch", "online"],
        queryFn: () => adminRiderApi.list({ online: true, page_size: 100 }),
        refetchInterval: 10000,
    });
    const { data: activeDeliveries } = useQuery({
        queryKey: ["admin", "dispatch", "active"],
        queryFn: () => adminRiderApi.deliveries({ status: "accepted" }),
        refetchInterval: 8000,
    });

    const assignMut = useMutation({
        mutationFn: ({ orderId, riderId }: { orderId: string; riderId: string }) =>
            adminRiderApi.assign(orderId, riderId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["admin", "dispatch"] });
            toast.success("Order assigned");
        },
        onError: (err) => toast.error(formatApiError(err, "Assign failed")),
    });
    const autoMut = useMutation({
        mutationFn: (orderId: string) => adminRiderApi.autoAssign(orderId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["admin", "dispatch"] });
            toast.success("Auto-assigned to the freest online rider");
        },
        onError: (err) => toast.error(formatApiError(err, "Auto-assign failed")),
    });

    return (
        <div className="space-y-5" data-testid="admin-dispatch-page">
            <div>
                <h1 className="text-2xl font-extrabold sm:text-3xl">Dispatch</h1>
                <p className="text-sm text-muted-foreground">
                    Assign orders to riders — auto-picks the freest online rider, or override manually.
                </p>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2 rounded-2xl border border-border/70 bg-card p-4 shadow-soft">
                    <h3 className="font-bold">Unassigned orders</h3>
                    <p className="text-xs text-muted-foreground">Orders in ‘confirmed’ or ‘packing’ with no rider yet</p>
                    {isLoading ? (
                        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
                    ) : unassigned.length === 0 ? (
                        <p className="mt-4 text-sm text-muted-foreground">Nothing waiting — all clear ✨</p>
                    ) : (
                        <ul className="mt-3 space-y-3">
                            {unassigned.map((o) => (
                                <li key={o.id} className="rounded-xl border border-border/60 p-3" data-testid={`dispatch-order-${o.id}`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-bold">{o.number}</p>
                                            <p className="text-xs text-muted-foreground">{o.item_count} items · {rupee(o.total)}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => autoMut.mutate(o.id)}
                                            disabled={autoMut.isPending}
                                            data-testid={`dispatch-auto-${o.id}`}
                                            className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-600"
                                        >
                                            <Zap className="h-3.5 w-3.5" /> Auto assign
                                        </button>
                                    </div>
                                    <div className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
                                        <MapPin className="h-3 w-3 shrink-0 mt-0.5" />
                                        <span className="line-clamp-2">{o.address.line1}, {o.address.city} - {o.address.pincode}</span>
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {(onlineRiders?.items || []).map((r: Rider) => (
                                            <button
                                                key={r.id}
                                                type="button"
                                                onClick={() => assignMut.mutate({ orderId: o.id, riderId: r.id })}
                                                disabled={assignMut.isPending}
                                                data-testid={`dispatch-assign-${o.id}-${r.id}`}
                                                className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] hover:bg-muted"
                                            >
                                                <Bike className="h-3 w-3" /> {r.full_name}
                                            </button>
                                        ))}
                                        {(onlineRiders?.items?.length || 0) === 0 ? (
                                            <span className="text-[11px] text-muted-foreground">No online riders — auto-assign will fail.</span>
                                        ) : null}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="space-y-4">
                    <div className="rounded-2xl border border-border/70 bg-card p-4 shadow-soft">
                        <h3 className="font-bold">Online riders</h3>
                        <p className="text-xs text-muted-foreground">Ready to pick up now</p>
                        <ul className="mt-3 space-y-2 text-sm">
                            {(onlineRiders?.items || []).map((r: Rider) => (
                                <li key={r.id} className="flex items-center gap-3">
                                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate font-semibold">{r.full_name}</p>
                                        <p className="text-[11px] text-muted-foreground">
                                            {r.vehicle_type} · {r.zone || "no zone"} · {r.total_deliveries} deliveries
                                        </p>
                                    </div>
                                </li>
                            ))}
                            {(onlineRiders?.items?.length || 0) === 0 ? (
                                <li className="text-xs text-muted-foreground">Nobody online.</li>
                            ) : null}
                        </ul>
                    </div>
                    <div className="rounded-2xl border border-border/70 bg-card p-4 shadow-soft">
                        <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-brand" />
                            <h3 className="font-bold">Currently in-flight</h3>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {activeDeliveries?.total ?? "…"} deliveries accepted
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
